/**
 * EditScheduledPostModal
 *
 * Modal for editing organic posts after they've been scheduled.
 * Supports editing caption, hashtags, rescheduing, platform changes,
 * and creative swapping with full version history.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
    X,
    Calendar,
    Clock,
    Edit2,
    Image,
    Hash,
    MessageSquare,
    Save,
    Trash2,
    RotateCcw,
    History,
    ChevronDown,
    ChevronUp,
    Instagram,
    Facebook,
    Linkedin,
    AlertCircle,
    Check,
    CheckCircle,
    XCircle,
    RefreshCw,
    Layers,
    ChevronLeft,
    ChevronRight,
    Send,
    Copy,
    FileText
} from 'lucide-react';
import {
    updateScheduledPost,
    unpublishPost,
    isPostFlaggedForLocalTesting,
    setLocalTestingFlag,
    clearLocalTestingFlag
} from '../../services/bulkSchedulingService';
import { OPTIMAL_POSTING_TIMES } from '../../services/postingTimeService';

// Check if running on localhost
const IS_LOCALHOST = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Post status configuration
const POST_STATUS_CONFIG = {
    draft: {
        icon: FileText,
        color: 'var(--color-text-tertiary)',
        bg: 'var(--color-bg-tertiary)',
        label: 'Draft',
        editable: true
    },
    scheduled: {
        icon: Clock,
        color: 'var(--color-primary)',
        bg: 'var(--color-primary-alpha, rgba(99, 102, 241, 0.1))',
        label: 'Scheduled',
        editable: true
    },
    published: {
        icon: CheckCircle,
        color: 'var(--success-600)',
        bg: 'var(--success-100)',
        label: 'Published',
        editable: false
    },
    failed: {
        icon: AlertCircle,
        color: 'var(--error-600)',
        bg: 'var(--error-100)',
        label: 'Failed',
        editable: true
    },
    cancelled: {
        icon: XCircle,
        color: 'var(--color-text-tertiary)',
        bg: 'var(--color-bg-tertiary)',
        label: 'Cancelled',
        editable: false
    }
};

// Platform icons
const PLATFORM_ICONS = {
    instagram: Instagram,
    facebook: Facebook,
    linkedin: Linkedin,
    tiktok: () => <span style={{ fontWeight: 600 }}>TT</span>,
    twitter: () => <span style={{ fontWeight: 600 }}>X</span>,
    google: () => <span style={{ fontWeight: 600 }}>G</span>
};

const PLATFORM_OPTIONS = [
    { id: 'instagram', label: 'Instagram', icon: Instagram },
    { id: 'facebook', label: 'Facebook', icon: Facebook },
    { id: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { id: 'tiktok', label: 'TikTok', icon: () => <span style={{ fontWeight: 600 }}>TT</span> },
    { id: 'twitter', label: 'Twitter/X', icon: () => <span style={{ fontWeight: 600 }}>X</span> }
];

const API_BASE = 'http://localhost:3001';

const EditScheduledPostModal = ({
    isOpen,
    onClose,
    post,
    userId,
    onUpdate,
    onCancel,
    onUnpublish,
    onRepublish,
    libraryItems = []  // For creative swapping
}) => {
    // Form state
    const [caption, setCaption] = useState('');
    const [hashtags, setHashtags] = useState('');
    const [scheduledDate, setScheduledDate] = useState('');
    const [scheduledTime, setScheduledTime] = useState('');
    const [platforms, setPlatforms] = useState([]);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');

    // UI state
    const [isSaving, setIsSaving] = useState(false);
    const [isScheduling, setIsScheduling] = useState(false);
    const [isRetrying, setIsRetrying] = useState(false);
    const [isUnpublishing, setIsUnpublishing] = useState(false);
    const [isRepublishing, setIsRepublishing] = useState(false);
    const [error, setError] = useState(null);
    const [showVersionHistory, setShowVersionHistory] = useState(false);
    const [showCreativeSwapper, setShowCreativeSwapper] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [showRepublishOptions, setShowRepublishOptions] = useState(false);

    // Local testing flag state (only shown on localhost)
    const [isLocalTestingFlagged, setIsLocalTestingFlagged] = useState(false);
    const [isLoadingLocalFlag, setIsLoadingLocalFlag] = useState(false);
    const [isTogglingLocalFlag, setIsTogglingLocalFlag] = useState(false);

    // Unpublish confirmation modal state
    const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
    const [unpublishResults, setUnpublishResults] = useState(null);

    // Helper to format date as YYYY-MM-DD using local timezone (not UTC)
    const formatLocalDate = (date) => {
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Helper to format time as HH:MM using local timezone
    const formatLocalTime = (date) => {
        const d = new Date(date);
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
    };

    // Get carousel images from post
    const carouselImages = useMemo(() => {
        if (!post) return [];
        // Check for carouselImages array (carousel posts)
        if (post.carouselImages && post.carouselImages.length > 0) {
            return post.carouselImages.map(img => img.imageUrl || img.url || img);
        }
        // Check if this is marked as a carousel type
        if (post.creativeType === 'carousel' && post.imageUrl) {
            // Single image but marked as carousel - just show main image
            return [post.imageUrl];
        }
        return [];
    }, [post]);

    const isCarousel = carouselImages.length > 1;

    // Initialize form from post
    useEffect(() => {
        if (post) {
            setCaption(post.caption || '');
            setHashtags(post.hashtags || '');
            setPlatforms(post.platforms || []);
            setSelectedImageUrl(post.imageUrl || '');
            setCarouselIndex(0); // Reset carousel index when post changes

            // Parse scheduled date using local timezone
            if (post.scheduledDate) {
                const date = new Date(post.scheduledDate);
                setScheduledDate(formatLocalDate(date));
                setScheduledTime(post.scheduledTime || formatLocalTime(date));
            }

            setHasChanges(false);
            setShowRepublishOptions(false);

            // Load local testing flag (only on localhost)
            if (IS_LOCALHOST && post.id) {
                setIsLoadingLocalFlag(true);
                isPostFlaggedForLocalTesting(post.id)
                    .then(flagged => setIsLocalTestingFlagged(flagged))
                    .catch(err => console.error('Error loading local testing flag:', err))
                    .finally(() => setIsLoadingLocalFlag(false));
            }
        }
    }, [post]);

    // Handle toggling the local testing flag
    const handleToggleLocalTestingFlag = async () => {
        if (!post?.id) return;

        setIsTogglingLocalFlag(true);
        try {
            if (isLocalTestingFlagged) {
                await clearLocalTestingFlag(post.id);
                setIsLocalTestingFlagged(false);
            } else {
                await setLocalTestingFlag(post.id);
                setIsLocalTestingFlagged(true);
            }
        } catch (err) {
            console.error('Error toggling local testing flag:', err);
            setError('Failed to toggle local testing flag');
        } finally {
            setIsTogglingLocalFlag(false);
        }
    };

    // Track changes
    useEffect(() => {
        if (!post) return;

        const changed =
            caption !== (post.caption || '') ||
            hashtags !== (post.hashtags || '') ||
            scheduledDate !== (post.scheduledDate ? new Date(post.scheduledDate).toISOString().split('T')[0] : '') ||
            scheduledTime !== (post.scheduledTime || '') ||
            JSON.stringify(platforms) !== JSON.stringify(post.platforms || []) ||
            selectedImageUrl !== (post.imageUrl || '');

        setHasChanges(changed);
    }, [caption, hashtags, scheduledDate, scheduledTime, platforms, selectedImageUrl, post]);

    // Toggle platform selection
    const togglePlatform = (platformId) => {
        setPlatforms(prev =>
            prev.includes(platformId)
                ? prev.filter(p => p !== platformId)
                : [...prev, platformId]
        );
    };

    // Handle save
    const handleSave = async () => {
        if (!post?.id || !userId) {
            setError('Cannot save: missing post ID or user ID');
            return;
        }

        if (!hasChanges) {
            onClose();
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            // Build date from date and time inputs
            const newScheduledDate = scheduledDate && scheduledTime
                ? new Date(`${scheduledDate}T${scheduledTime}:00`)
                : new Date(post.scheduledDate);

            const updates = {
                caption,
                hashtags,
                platforms,
                scheduledDate: newScheduledDate.toISOString(),
                scheduledTime,
                imageUrl: selectedImageUrl
            };

            const result = await updateScheduledPost(post.id, updates, userId);

            if (result.success) {
                onUpdate?.(post.id, { ...post, ...updates, currentVersion: result.version });
                onClose();
            }
        } catch (err) {
            console.error('Failed to update post:', err);
            setError(err.message || 'Failed to save changes');
        } finally {
            setIsSaving(false);
        }
    };

    // Handle schedule - save changes and set status to scheduled (for draft posts)
    const handleSchedule = async () => {
        if (!post?.id || !userId) {
            setError('Cannot schedule: missing post ID or user ID');
            return;
        }

        // Validate required fields for scheduling
        if (!scheduledDate || !scheduledTime) {
            setError('Please select a date and time to schedule this post');
            return;
        }

        if (!platforms || platforms.length === 0) {
            setError('Please select at least one platform');
            return;
        }

        setIsScheduling(true);
        setError(null);

        try {
            // Build date from date and time inputs
            const newScheduledDate = new Date(`${scheduledDate}T${scheduledTime}:00`);

            const updates = {
                caption,
                hashtags,
                platforms,
                scheduledDate: newScheduledDate.toISOString(),
                scheduledTime,
                imageUrl: selectedImageUrl,
                status: 'scheduled' // Change status from draft to scheduled
            };

            const result = await updateScheduledPost(post.id, updates, userId);

            if (result.success) {
                onUpdate?.(post.id, { ...post, ...updates, currentVersion: result.version });
                onClose();
            }
        } catch (err) {
            console.error('Failed to schedule post:', err);
            setError(err.message || 'Failed to schedule post');
        } finally {
            setIsScheduling(false);
        }
    };

    // Handle cancel post
    const handleCancelPost = async () => {
        if (!post?.id) return;

        try {
            await onCancel?.(post.id);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to cancel post');
        }
    };

    // Handle retry failed post - resets status to scheduled for re-publishing
    const handleRetry = async () => {
        if (!post?.id || !userId) {
            setError('Cannot retry: missing post ID or user ID');
            return;
        }

        setIsRetrying(true);
        setError(null);

        try {
            // Reset status to scheduled and clear error
            const updates = {
                status: 'scheduled',
                lastError: null,
                lastAttemptAt: null
                // Note: publishAttempts is preserved so we can track total attempts
            };

            const result = await updateScheduledPost(post.id, updates, userId);

            if (result.success) {
                onUpdate?.(post.id, { ...post, ...updates });
                onClose();
            }
        } catch (err) {
            console.error('Failed to retry post:', err);
            setError(err.message || 'Failed to retry post');
        } finally {
            setIsRetrying(false);
        }
    };

    // Handle revert to version
    const handleRevert = async (version) => {
        if (!post?.versions || !post.id) return;

        const targetVersion = post.versions.find(v => v.version === version);
        if (!targetVersion?.snapshot) return;

        // Apply snapshot values
        setCaption(targetVersion.snapshot.caption || '');
        setHashtags(targetVersion.snapshot.hashtags || '');
        setPlatforms(targetVersion.snapshot.platforms || []);
        setSelectedImageUrl(targetVersion.snapshot.imageUrl || '');

        if (targetVersion.snapshot.scheduledDate) {
            const date = new Date(targetVersion.snapshot.scheduledDate);
            setScheduledDate(formatLocalDate(date));
            setScheduledTime(targetVersion.snapshot.scheduledTime || formatLocalTime(date));
        }
    };

    // Handle unpublish confirmation - show modal first
    const handleUnpublishClick = () => {
        setShowUnpublishConfirm(true);
    };

    // Handle unpublish - delete from platforms and reset to draft
    const handleUnpublish = async () => {
        if (!post?.id || !userId) {
            setError('Cannot unpublish: missing post ID or user ID');
            return;
        }

        setIsUnpublishing(true);
        setError(null);
        setShowUnpublishConfirm(false);

        try {
            // Call backend API to delete from platforms and reset to draft
            const result = await unpublishPost(post.id, userId);

            if (result.success) {
                // Store results for display
                setUnpublishResults(result.results);

                // Update local state
                const updates = {
                    status: 'draft',
                    publishedAt: null,
                    publishResults: null
                };

                onUpdate?.(post.id, { ...post, ...updates });
                onUnpublish?.(post.id);
                onClose();
            }
        } catch (err) {
            console.error('Failed to unpublish post:', err);
            setError(err.message || 'Failed to unpublish post');
        } finally {
            setIsUnpublishing(false);
        }
    };

    // Handle republish - schedules the post for publishing again
    const handleRepublish = async () => {
        if (!post?.id || !userId) {
            setError('Cannot republish: missing post ID or user ID');
            return;
        }

        if (!scheduledDate || !scheduledTime) {
            setError('Please select a date and time for republishing');
            return;
        }

        setIsRepublishing(true);
        setError(null);

        try {
            // Build new scheduled date
            const newScheduledDate = new Date(`${scheduledDate}T${scheduledTime}:00`);

            const updates = {
                status: 'scheduled',
                scheduledDate: newScheduledDate.toISOString(),
                scheduledTime,
                publishedAt: null,
                publishResults: null,
                publishAttempts: 0,
                lastError: null,
                lastAttemptAt: null
            };

            const result = await updateScheduledPost(post.id, updates, userId);

            if (result.success) {
                onUpdate?.(post.id, { ...post, ...updates });
                onRepublish?.(post.id);
                onClose();
            }
        } catch (err) {
            console.error('Failed to republish post:', err);
            setError(err.message || 'Failed to republish post');
        } finally {
            setIsRepublishing(false);
        }
    };

    // Handle carousel navigation
    const nextCarouselImage = () => {
        setCarouselIndex(prev => (prev + 1) % carouselImages.length);
    };

    const prevCarouselImage = () => {
        setCarouselIndex(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
    };

    // Optimal times for selected day
    const optimalTimes = useMemo(() => {
        if (!scheduledDate) return [];

        const dayOfWeek = new Date(scheduledDate).getDay();
        const dayName = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][dayOfWeek];

        // Get times for primary platform
        const primaryPlatform = platforms[0] || 'instagram';
        const platformTimes = OPTIMAL_POSTING_TIMES[primaryPlatform];

        if (!platformTimes) return [];

        // Return times for this day
        return platformTimes[dayName] || platformTimes.weekday || [];
    }, [scheduledDate, platforms]);

    if (!isOpen || !post) return null;

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: 'var(--space-4)'
            }}
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                className="card"
                style={{
                    width: '100%',
                    maxWidth: '600px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <Edit2 size={20} style={{ color: 'var(--color-primary)' }} />
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <h3 style={{ margin: 0 }}>
                                    {post.status === 'published' ? 'Published Post' : 'Edit Scheduled Post'}
                                </h3>
                                {/* Status Badge */}
                                {post.status && POST_STATUS_CONFIG[post.status] && (
                                    <span style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        padding: '2px 8px',
                                        borderRadius: 'var(--radius-full)',
                                        background: POST_STATUS_CONFIG[post.status].bg,
                                        color: POST_STATUS_CONFIG[post.status].color,
                                        fontSize: 'var(--font-size-xs)',
                                        fontWeight: 500
                                    }}>
                                        {React.createElement(POST_STATUS_CONFIG[post.status].icon, { size: 12 })}
                                        {POST_STATUS_CONFIG[post.status].label}
                                    </span>
                                )}
                            </div>
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                v{post.currentVersion || 1}
                                {post.source === 'bulk_planner' && ' • From Bulk Planner'}
                                {post.status === 'published' && post.publishedAt && (
                                    <> • Published {new Date(post.publishedAt).toLocaleString()}</>
                                )}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)',
                            color: 'var(--color-text-secondary)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 'var(--space-4)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-4)'
                }}>
                    {/* Error display */}
                    {error && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            padding: 'var(--space-3)',
                            background: 'var(--error-100)',
                            borderRadius: 'var(--radius-md)',
                            color: 'var(--error-700)'
                        }}>
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {/* Failed Post Error Info */}
                    {post.status === 'failed' && (post.lastError || post.publishAttempts > 0) && (() => {
                        // Detect connection issues from error message (for legacy data before fix)
                        const errorLower = (post.lastError || '').toLowerCase();
                        const technicalErrorLower = (post.lastErrorTechnical || '').toLowerCase();
                        const isConnectionError = post.requiresReconnect ||
                            errorLower.includes('not connected') ||
                            errorLower.includes('connection') ||
                            technicalErrorLower.includes('connection found') ||
                            technicalErrorLower.includes('no connection');

                        // Get failed platform names for display
                        const failedPlatformNames = post.failedPlatforms?.length > 0
                            ? post.failedPlatforms.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' and ')
                            : post.platforms?.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' and ') || 'the platform';

                        return (
                            <div style={{
                                padding: 'var(--space-3)',
                                background: isConnectionError ? 'var(--warning-100)' : 'var(--error-100)',
                                borderRadius: 'var(--radius-md)',
                                border: `1px solid ${isConnectionError ? 'var(--warning-300)' : 'var(--error-300)'}`
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    marginBottom: 'var(--space-2)',
                                    color: isConnectionError ? 'var(--warning-700)' : 'var(--error-700)',
                                    fontWeight: 600
                                }}>
                                    <AlertCircle size={16} />
                                    {isConnectionError ? 'Account Not Connected' : 'Publishing Failed'}
                                </div>

                                {/* User-friendly error message - override generic messages */}
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-primary)',
                                    marginBottom: 'var(--space-2)',
                                    lineHeight: 1.5
                                }}>
                                    {isConnectionError
                                        ? `Your ${failedPlatformNames} account is not connected to this app.`
                                        : post.lastError || 'Something went wrong while publishing.'
                                    }
                                </div>

                                {/* Action required with clickable link */}
                                <div style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: isConnectionError ? 'var(--warning-700)' : 'var(--color-text-secondary)',
                                    marginBottom: 'var(--space-2)',
                                    padding: 'var(--space-2)',
                                    background: isConnectionError ? 'var(--warning-50)' : 'var(--color-bg-tertiary)',
                                    borderRadius: 'var(--radius-sm)',
                                    borderLeft: `3px solid ${isConnectionError ? 'var(--warning-500)' : 'var(--color-primary)'}`
                                }}>
                                    <strong>What to do:</strong>{' '}
                                    {isConnectionError ? (
                                        <>
                                            Connect your {failedPlatformNames} account, then retry this post.
                                        </>
                                    ) : (
                                        post.lastErrorAction || 'Check your account connection and try again.'
                                    )}
                                </div>

                                {/* Connect Account button for connection errors */}
                                {isConnectionError && (
                                    <a
                                        href="/campaigns"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: 'var(--color-primary)',
                                            color: 'white',
                                            borderRadius: 'var(--radius-md)',
                                            textDecoration: 'none',
                                            fontSize: 'var(--font-size-sm)',
                                            fontWeight: 500,
                                            marginBottom: 'var(--space-2)'
                                        }}
                                    >
                                        Connect {failedPlatformNames} Account →
                                    </a>
                                )}

                                {/* Failed platforms */}
                                {post.failedPlatforms?.length > 0 && (
                                    <div style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        marginBottom: 'var(--space-2)'
                                    }}>
                                        Failed on: {post.failedPlatforms.map(p =>
                                            p.charAt(0).toUpperCase() + p.slice(1)
                                        ).join(', ')}
                                    </div>
                                )}

                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 'var(--space-3)'
                                }}>
                                    {post.publishAttempts > 0 && (
                                        <span>Attempts: {post.publishAttempts}</span>
                                    )}
                                    {post.lastAttemptAt && (
                                        <span>Last attempt: {
                                            // Handle Firestore Timestamp objects and ISO strings
                                            post.lastAttemptAt?.toDate
                                                ? post.lastAttemptAt.toDate().toLocaleString()
                                                : post.lastAttemptAt?.seconds
                                                    ? new Date(post.lastAttemptAt.seconds * 1000).toLocaleString()
                                                    : new Date(post.lastAttemptAt).toLocaleString()
                                        }</span>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Creative Preview */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-4)',
                        alignItems: 'flex-start'
                    }}>
                        <div style={{ position: 'relative' }}>
                            {/* Main image or carousel */}
                            {isCarousel ? (
                                <div style={{ position: 'relative' }}>
                                    <img
                                        src={carouselImages[carouselIndex]}
                                        alt={`${post.creativeTitle} - ${carouselIndex + 1}`}
                                        style={{
                                            width: 160,
                                            height: 160,
                                            borderRadius: 'var(--radius-md)',
                                            objectFit: 'cover',
                                            border: '1px solid var(--color-border)'
                                        }}
                                    />
                                    {/* Carousel indicator badge */}
                                    <div style={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        background: 'rgba(0,0,0,0.7)',
                                        borderRadius: 'var(--radius-sm)',
                                        padding: '4px 8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        <Layers size={12} color="white" />
                                        <span style={{ fontSize: '11px', color: 'white', fontWeight: 600 }}>
                                            {carouselIndex + 1}/{carouselImages.length}
                                        </span>
                                    </div>
                                    {/* Navigation arrows */}
                                    <button
                                        onClick={prevCarouselImage}
                                        style={{
                                            position: 'absolute',
                                            left: 4,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            background: 'rgba(0,0,0,0.6)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white'
                                        }}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    <button
                                        onClick={nextCarouselImage}
                                        style={{
                                            position: 'absolute',
                                            right: 4,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            background: 'rgba(0,0,0,0.6)',
                                            border: 'none',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white'
                                        }}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            ) : selectedImageUrl ? (
                                <img
                                    src={selectedImageUrl}
                                    alt={post.creativeTitle}
                                    style={{
                                        width: 120,
                                        height: 120,
                                        borderRadius: 'var(--radius-md)',
                                        objectFit: 'cover',
                                        border: '1px solid var(--color-border)'
                                    }}
                                />
                            ) : (
                                <div style={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 'var(--radius-md)',
                                    background: 'var(--color-bg-tertiary)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Image size={32} style={{ color: 'var(--color-text-tertiary)' }} />
                                </div>
                            )}
                            {/* Swap creative button - only show for non-published editable posts */}
                            {POST_STATUS_CONFIG[post.status]?.editable !== false && (
                                <button
                                    onClick={() => setShowCreativeSwapper(!showCreativeSwapper)}
                                    style={{
                                        position: 'absolute',
                                        bottom: -8,
                                        right: -8,
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        background: 'var(--color-primary)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white'
                                    }}
                                >
                                    <RotateCcw size={14} />
                                </button>
                            )}
                        </div>

                        <div style={{ flex: 1 }}>
                            <h4 style={{ margin: 0, marginBottom: 'var(--space-1)' }}>
                                {post.creativeTitle}
                            </h4>
                            <div style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)',
                                marginBottom: 'var(--space-1)'
                            }}>
                                {post.pillarName && <span>{post.pillarName} • </span>}
                                {post.funnelStage || 'TOFU'}
                                {isCarousel && (
                                    <span style={{
                                        marginLeft: 'var(--space-2)',
                                        padding: '2px 6px',
                                        background: 'var(--color-bg-tertiary)',
                                        borderRadius: 'var(--radius-sm)',
                                        fontSize: 'var(--font-size-xs)'
                                    }}>
                                        <Layers size={10} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                                        {carouselImages.length} cards
                                    </span>
                                )}
                            </div>
                            {/* Carousel thumbnails strip */}
                            {isCarousel && (
                                <div style={{
                                    display: 'flex',
                                    gap: 'var(--space-1)',
                                    marginTop: 'var(--space-2)',
                                    overflowX: 'auto',
                                    paddingBottom: 'var(--space-1)'
                                }}>
                                    {carouselImages.map((img, idx) => (
                                        <img
                                            key={idx}
                                            src={img}
                                            alt={`Card ${idx + 1}`}
                                            onClick={() => setCarouselIndex(idx)}
                                            style={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 'var(--radius-sm)',
                                                objectFit: 'cover',
                                                cursor: 'pointer',
                                                border: idx === carouselIndex
                                                    ? '2px solid var(--color-primary)'
                                                    : '1px solid var(--color-border)',
                                                opacity: idx === carouselIndex ? 1 : 0.7,
                                                transition: 'all 0.15s ease'
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Creative Swapper */}
                    {showCreativeSwapper && libraryItems.length > 0 && (
                        <div style={{
                            padding: 'var(--space-3)',
                            background: 'var(--color-bg-secondary)',
                            borderRadius: 'var(--radius-md)',
                            border: '1px solid var(--color-border)'
                        }}>
                            <div style={{
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                Swap Creative
                            </div>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(60px, 1fr))',
                                gap: 'var(--space-2)',
                                maxHeight: 200,
                                overflowY: 'auto'
                            }}>
                                {libraryItems
                                    .filter(item => item.imageUrl)
                                    .slice(0, 20)
                                    .map(item => (
                                        <img
                                            key={item.id}
                                            src={item.imageUrl}
                                            alt={item.title}
                                            onClick={() => {
                                                setSelectedImageUrl(item.imageUrl);
                                                setShowCreativeSwapper(false);
                                            }}
                                            style={{
                                                width: '100%',
                                                aspectRatio: '1',
                                                objectFit: 'cover',
                                                borderRadius: 'var(--radius-sm)',
                                                cursor: 'pointer',
                                                border: selectedImageUrl === item.imageUrl
                                                    ? '2px solid var(--color-primary)'
                                                    : '1px solid var(--color-border)'
                                            }}
                                        />
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Caption */}
                    <div>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            <MessageSquare size={14} />
                            Caption
                        </label>
                        <textarea
                            value={caption}
                            onChange={(e) => setCaption(e.target.value)}
                            rows={4}
                            className="input-field"
                            style={{ width: '100%', resize: 'vertical' }}
                            placeholder="Write your caption..."
                        />
                    </div>

                    {/* Hashtags */}
                    <div>
                        <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)'
                        }}>
                            <Hash size={14} />
                            Hashtags
                        </label>
                        <input
                            type="text"
                            value={hashtags}
                            onChange={(e) => setHashtags(e.target.value)}
                            className="input-field"
                            style={{ width: '100%' }}
                            placeholder="#marketing #socialmedia"
                        />
                    </div>

                    {/* Date and Time */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                        <div>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                <Calendar size={14} />
                                Date
                            </label>
                            <input
                                type="date"
                                value={scheduledDate}
                                onChange={(e) => setScheduledDate(e.target.value)}
                                className="input-field"
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-1)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                marginBottom: 'var(--space-2)'
                            }}>
                                <Clock size={14} />
                                Time
                            </label>
                            <input
                                type="time"
                                value={scheduledTime}
                                onChange={(e) => setScheduledTime(e.target.value)}
                                className="input-field"
                                style={{ width: '100%' }}
                            />
                            {/* Optimal time suggestions */}
                            {optimalTimes.length > 0 && (
                                <div style={{
                                    marginTop: 'var(--space-2)',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 'var(--space-1)'
                                }}>
                                    <span style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-tertiary)'
                                    }}>
                                        Optimal:
                                    </span>
                                    {optimalTimes.map(time => (
                                        <button
                                            key={time}
                                            type="button"
                                            onClick={() => setScheduledTime(time)}
                                            style={{
                                                padding: '2px 8px',
                                                fontSize: 'var(--font-size-xs)',
                                                background: scheduledTime === time
                                                    ? 'var(--color-primary)'
                                                    : 'var(--color-bg-secondary)',
                                                color: scheduledTime === time
                                                    ? 'white'
                                                    : 'var(--color-text-secondary)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-sm)',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {time}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Platforms */}
                    <div>
                        <label style={{
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: 500,
                            marginBottom: 'var(--space-2)',
                            display: 'block'
                        }}>
                            Platforms
                        </label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                            {PLATFORM_OPTIONS.map(platform => {
                                const Icon = platform.icon;
                                const isSelected = platforms.includes(platform.id);
                                return (
                                    <button
                                        key={platform.id}
                                        onClick={() => togglePlatform(platform.id)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 'var(--space-2)',
                                            padding: 'var(--space-2) var(--space-3)',
                                            background: isSelected
                                                ? 'var(--color-primary)'
                                                : 'var(--color-bg-secondary)',
                                            color: isSelected
                                                ? 'white'
                                                : 'var(--color-text-primary)',
                                            border: isSelected
                                                ? 'none'
                                                : '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-md)',
                                            cursor: 'pointer',
                                            fontSize: 'var(--font-size-sm)'
                                        }}
                                    >
                                        <Icon size={16} />
                                        {platform.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Version History */}
                    {post.versions && post.versions.length > 0 && (
                        <div>
                            <button
                                onClick={() => setShowVersionHistory(!showVersionHistory)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-2)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: 'var(--font-size-sm)',
                                    fontWeight: 500,
                                    color: 'var(--color-text-secondary)',
                                    padding: 0
                                }}
                            >
                                <History size={14} />
                                Version History ({post.versions.length})
                                {showVersionHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>

                            {showVersionHistory && (
                                <div style={{
                                    marginTop: 'var(--space-2)',
                                    padding: 'var(--space-3)',
                                    background: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-md)',
                                    maxHeight: 200,
                                    overflowY: 'auto'
                                }}>
                                    {[...post.versions].reverse().map(version => (
                                        <div
                                            key={version.version}
                                            style={{
                                                padding: 'var(--space-2)',
                                                borderBottom: '1px solid var(--color-border)',
                                                fontSize: 'var(--font-size-sm)'
                                            }}
                                        >
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: 'var(--space-1)'
                                            }}>
                                                <span style={{ fontWeight: 500 }}>
                                                    v{version.version}
                                                </span>
                                                <span style={{ color: 'var(--color-text-tertiary)' }}>
                                                    {new Date(version.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                            <div style={{
                                                fontSize: 'var(--font-size-xs)',
                                                color: 'var(--color-text-secondary)'
                                            }}>
                                                {Object.keys(version.changes || {}).map(field => (
                                                    <span key={field} style={{ marginRight: 'var(--space-2)' }}>
                                                        {field}
                                                    </span>
                                                ))}
                                            </div>
                                            <button
                                                onClick={() => handleRevert(version.version)}
                                                style={{
                                                    marginTop: 'var(--space-1)',
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-primary)',
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: 0
                                                }}
                                            >
                                                Revert to this version
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Local Testing Toggle - only shown on localhost for scheduled posts */}
                {IS_LOCALHOST && post.status === 'scheduled' && (
                    <div style={{
                        padding: 'var(--space-3)',
                        borderTop: '1px solid var(--color-border)',
                        background: 'var(--warning-50)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 'var(--space-3)'
                    }}>
                        <div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                fontWeight: 500,
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--warning-700)'
                            }}>
                                <AlertCircle size={14} />
                                Local Testing Mode
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)',
                                marginTop: '2px'
                            }}>
                                {isLocalTestingFlagged
                                    ? 'This post will only be published by your local server (not production)'
                                    : 'Enable to prevent production from publishing this post'}
                            </div>
                        </div>
                        <button
                            onClick={handleToggleLocalTestingFlag}
                            disabled={isLoadingLocalFlag || isTogglingLocalFlag}
                            style={{
                                padding: '6px 12px',
                                borderRadius: 'var(--radius-md)',
                                border: 'none',
                                background: isLocalTestingFlagged ? 'var(--warning-500)' : 'var(--color-bg-tertiary)',
                                color: isLocalTestingFlagged ? 'white' : 'var(--color-text-secondary)',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 500,
                                cursor: isLoadingLocalFlag || isTogglingLocalFlag ? 'not-allowed' : 'pointer',
                                opacity: isLoadingLocalFlag || isTogglingLocalFlag ? 0.6 : 1,
                                transition: 'all 0.15s ease',
                                minWidth: '80px'
                            }}
                        >
                            {isLoadingLocalFlag ? 'Loading...' : isTogglingLocalFlag ? '...' : isLocalTestingFlagged ? 'Enabled' : 'Enable'}
                        </button>
                    </div>
                )}

                {/* Republish Options Panel - shown when republish is clicked */}
                {showRepublishOptions && post.status === 'published' && (
                    <div style={{
                        padding: 'var(--space-4)',
                        borderTop: '1px solid var(--color-border)',
                        background: 'var(--color-primary-alpha, rgba(99, 102, 241, 0.05))'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-3)',
                            fontWeight: 500
                        }}>
                            <Copy size={16} style={{ color: 'var(--color-primary)' }} />
                            Schedule for Republishing
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: 'var(--space-3)',
                            marginBottom: 'var(--space-3)'
                        }}>
                            <div>
                                <label style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)',
                                    marginBottom: 'var(--space-1)',
                                    display: 'block'
                                }}>
                                    New Date
                                </label>
                                <input
                                    type="date"
                                    value={scheduledDate}
                                    onChange={(e) => setScheduledDate(e.target.value)}
                                    className="input-field"
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div>
                                <label style={{
                                    fontSize: 'var(--font-size-sm)',
                                    color: 'var(--color-text-secondary)',
                                    marginBottom: 'var(--space-1)',
                                    display: 'block'
                                }}>
                                    New Time
                                </label>
                                <input
                                    type="time"
                                    value={scheduledTime}
                                    onChange={(e) => setScheduledTime(e.target.value)}
                                    className="input-field"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowRepublishOptions(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleRepublish}
                                disabled={isRepublishing || !scheduledDate || !scheduledTime}
                            >
                                {isRepublishing ? (
                                    <>Scheduling...</>
                                ) : (
                                    <>
                                        <Calendar size={16} />
                                        Schedule Republish
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4)',
                    borderTop: '1px solid var(--color-border)',
                    background: 'var(--color-bg-tertiary)'
                }}>
                    {/* Check if post is editable based on status */}
                    {POST_STATUS_CONFIG[post.status]?.editable !== false ? (
                        <>
                            <button
                                onClick={handleCancelPost}
                                className="btn btn-ghost"
                                style={{ color: 'var(--error-500)' }}
                            >
                                <Trash2 size={16} />
                                Cancel Post
                            </button>

                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <button className="btn btn-secondary" onClick={onClose}>
                                    Discard
                                </button>
                                {/* Retry button for failed posts */}
                                {post.status === 'failed' && (
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleRetry}
                                        disabled={isRetrying}
                                        style={{
                                            color: 'var(--warning-600)',
                                            borderColor: 'var(--warning-300)'
                                        }}
                                    >
                                        {isRetrying ? (
                                            <>Retrying...</>
                                        ) : (
                                            <>
                                                <RefreshCw size={16} />
                                                Retry
                                            </>
                                        )}
                                    </button>
                                )}
                                {/* For draft posts: Save (secondary) + Schedule (primary) */}
                                {post.status === 'draft' ? (
                                    <>
                                        <button
                                            className="btn btn-secondary"
                                            onClick={handleSave}
                                            disabled={isSaving || !hasChanges}
                                        >
                                            {isSaving ? 'Saving...' : (
                                                <>
                                                    <Save size={16} />
                                                    Save Draft
                                                </>
                                            )}
                                        </button>
                                        <button
                                            className="btn btn-primary"
                                            onClick={handleSchedule}
                                            disabled={isScheduling}
                                        >
                                            {isScheduling ? 'Scheduling...' : (
                                                <>
                                                    <Send size={16} />
                                                    Schedule
                                                </>
                                            )}
                                        </button>
                                    </>
                                ) : (
                                    /* For scheduled/failed posts: Save Changes is primary */
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={isSaving || !hasChanges}
                                    >
                                        {isSaving ? 'Saving...' : (
                                            <>
                                                <Save size={16} />
                                                Save Changes
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        </>
                    ) : post.status === 'published' ? (
                        /* Footer for published posts - unpublish and republish options */
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <button
                                onClick={handleUnpublishClick}
                                className="btn btn-ghost"
                                style={{ color: 'var(--warning-600)' }}
                                disabled={isUnpublishing}
                            >
                                {isUnpublishing ? (
                                    <>Unpublishing...</>
                                ) : (
                                    <>
                                        <XCircle size={16} />
                                        Unpublish
                                    </>
                                )}
                            </button>
                            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                                <button className="btn btn-secondary" onClick={onClose}>
                                    Close
                                </button>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowRepublishOptions(!showRepublishOptions)}
                                >
                                    <Copy size={16} />
                                    Republish
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Read-only footer for cancelled posts */
                        <div style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'flex-end'
                        }}>
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Unpublish Confirmation Modal */}
            {showUnpublishConfirm && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10001,
                        padding: 'var(--space-4)'
                    }}
                    onClick={(e) => e.target === e.currentTarget && setShowUnpublishConfirm(false)}
                >
                    <div
                        className="card"
                        style={{
                            width: '100%',
                            maxWidth: '450px',
                            padding: 'var(--space-5)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            marginBottom: 'var(--space-4)'
                        }}>
                            <div style={{
                                width: 40,
                                height: 40,
                                borderRadius: '50%',
                                background: 'var(--warning-100)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <AlertCircle size={20} style={{ color: 'var(--warning-600)' }} />
                            </div>
                            <h3 style={{ margin: 0 }}>Unpublish Post?</h3>
                        </div>

                        <div style={{
                            marginBottom: 'var(--space-4)',
                            color: 'var(--color-text-secondary)',
                            lineHeight: 1.6
                        }}>
                            <p style={{ marginBottom: 'var(--space-3)' }}>
                                This will attempt to <strong>delete the post from {post?.platforms?.join(' and ') || 'social platforms'}</strong> and reset it to draft status in your scheduler.
                            </p>

                            {post?.platforms?.includes('instagram') && (
                                <div style={{
                                    padding: 'var(--space-3)',
                                    background: 'var(--warning-50)',
                                    borderRadius: 'var(--radius-md)',
                                    border: '1px solid var(--warning-200)',
                                    marginBottom: 'var(--space-3)'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-2)',
                                        marginBottom: 'var(--space-1)',
                                        fontWeight: 500,
                                        color: 'var(--warning-700)'
                                    }}>
                                        <Instagram size={14} />
                                        Instagram Note
                                    </div>
                                    <span style={{ fontSize: 'var(--font-size-sm)' }}>
                                        Instagram does not allow automated post deletion. You will need to delete the Instagram post manually through the Instagram app or Meta Business Suite.
                                    </span>
                                </div>
                            )}

                            <p style={{ fontSize: 'var(--font-size-sm)', marginBottom: 0 }}>
                                The post will be reset to <strong>draft</strong> status and will not be republished automatically.
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 'var(--space-2)'
                        }}>
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowUnpublishConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn"
                                onClick={handleUnpublish}
                                disabled={isUnpublishing}
                                style={{
                                    background: 'var(--warning-600)',
                                    color: 'white',
                                    border: 'none'
                                }}
                            >
                                {isUnpublishing ? 'Unpublishing...' : 'Unpublish Post'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EditScheduledPostModal;
